import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, Globe, MessageCircle, GraduationCap,
  ScrollText, Link2, Sparkles, ChevronRight, ArrowRight, Heart,
} from "lucide-react";
import { OrnamentDivider, AnimatedCard, Section, SectionHeading } from "@/components/ui/shared";
import DailyVerse from "@/components/ui/DailyVerse";
import { SCHOLAR_ENABLED } from "@/lib/features";
import { useClarifierFilters, usePageView, useSeerahFilters, useWorldFilters } from "@/lib/api";

/* ============================================
   Feature Cards Data
   ============================================ */

const features = [
  {
    icon: ScrollText,
    title: "Seerah Explorer",
    description:
      "Walk through the life of Prophet Muhammad ﷺ with authenticated hadith sources and Quranic references for every event.",
    to: "/seerah",
    color: "from-emerald-500 to-teal-600",
    stat: "13 Events · 8 Eras",
  },
  {
    icon: Globe,
    title: "World Timeline",
    description:
      "See what was happening across civilizations — Byzantine, Persian, Chinese — alongside the Prophetic biography.",
    to: "/timeline",
    color: "from-[var(--color-gold)] to-amber-600",
    stat: "20 Events · 8 Civilizations",
  },
  {
    icon: BookOpen,
    title: "Quran Companion",
    description:
      "Explore the Quran with tafsir from Ibn Kathir and Al-Sa'di, thematic connections, and full-text search.",
    to: "/quran",
    color: "from-purple-500 to-indigo-600",
    stat: "5 Surahs · 24 Ayahs",
  },
  {
    icon: MessageCircle,
    title: "Misconception Clarifier",
    description:
      "Common misconceptions about Islam, clarified with verified sources from Quran and Sunnah.",
    to: "/clarifier",
    color: "from-rose-500 to-pink-600",
    stat: "6 Topics · 5 Categories",
  },
  {
    icon: Link2,
    title: "Isnad Explorer",
    description:
      "Visualize chains of narration — from the great hadith collectors back to Prophet Muhammad ﷺ himself.",
    to: "/isnad",
    color: "from-blue-500 to-cyan-600",
    stat: "13 Narrators · 2 Chains",
  },
  {
    icon: GraduationCap,
    title: "Learning Paths",
    description:
      "Structured courses that weave together Quran, hadith, seerah, and scholarly analysis into guided journeys.",
    to: "/learning",
    color: "from-orange-500 to-amber-600",
    stat: "1 Path · 5 Lessons",
  },
  {
    icon: Heart,
    title: "Dhikr Companion",
    description:
      "Morning and evening adhkar from authenticated hadith. Tap-to-count interface with progress tracking and tasbeeh counter.",
    to: "/dhikr",
    color: "from-teal-500 to-emerald-600",
    stat: "7 Adhkar · Tasbeeh Counter",
  },
];

/* ============================================
   Hero Section
   ============================================ */

/** Live content counts for the hero (blank until loaded rather than invented). */
function useLibraryStats() {
  const seerah = useSeerahFilters();
  const clarifier = useClarifierFilters();
  const world = useWorldFilters();
  const worldTotal = world.data?.civilizations.reduce((sum, c) => sum + c.count, 0);
  const fmt = (n: number | undefined) => (n === undefined ? "—" : n.toLocaleString());
  return [
    { label: "Seerah events", value: fmt(seerah.data?.totalEvents) },
    { label: "Misconceptions answered", value: fmt(clarifier.data?.totalMisconceptions) },
    { label: "World events", value: fmt(worldTotal) },
  ];
}

function Hero() {
  const stats = useLibraryStats();
  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[var(--color-midnight)]">
      {/* Background Pattern */}
      <div className="absolute inset-0 pattern-islamic opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-midnight)] via-transparent to-[var(--color-midnight)]" />

      {/* Radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[var(--color-gold)]/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
        {/* Bismillah */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-arabic text-[var(--color-gold)]/70 text-2xl md:text-3xl mb-8"
        >
          بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ
        </motion.p>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-[var(--font-display)] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-[var(--color-cream)] leading-[1.1] tracking-tight"
        >
          Deen Over
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gold)] via-[var(--color-gold-light)] to-[var(--color-gold)]">
            Dunya
          </span>
        </motion.h1>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-6 text-lg md:text-xl text-[var(--color-cream)]/60 max-w-2xl mx-auto leading-relaxed"
        >
          Authentic Islamic knowledge — every claim traced to its source,
          every hadith graded, every ayah in context. Scholarship meets
          technology in service of truth.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/seerah" className="btn-gold flex items-center gap-2 text-base px-8 py-3.5">
            Begin Exploring
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/timeline" className="btn-outline flex items-center gap-2 text-base px-8 py-3.5">
            View Timeline
            <Globe className="w-4 h-4" />
          </Link>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-[var(--color-gold)]">
                {stat.value}
              </div>
              <div className="text-xs md:text-sm text-[var(--color-cream)]/40 mt-1 uppercase tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-5 h-8 rounded-full border-2 border-[var(--color-gold)]/30 flex justify-center"
        >
          <div className="w-1 h-2 bg-[var(--color-gold)]/50 rounded-full mt-1.5" />
        </motion.div>
      </motion.div>
    </div>
  );
}

/* ============================================
   Feature Cards Section
   ============================================ */

function Features() {
  return (
    <Section className="pattern-islamic">
      <SectionHeading
        eyebrow="Explore"
        title="Knowledge, Verified & Connected"
        subtitle="Every feature is built on authenticated sources — Quran, Sahih hadith, and scholarly consensus — cross-referenced across domains."
      />

      <OrnamentDivider />

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, i) => (
          <AnimatedCard key={feature.to} delay={i * 0.1}>
            <Link to={feature.to} className="block card-elevated p-6 h-full group">
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-105 transition-transform`}
              >
                <feature.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-[var(--font-display)] text-xl font-semibold text-[var(--color-midnight)] mb-2">
                {feature.title}
              </h3>
              <p className="text-[var(--color-ink-muted)] text-sm leading-relaxed mb-4">
                {feature.description}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[var(--color-gold-dim)] font-medium">
                  {feature.stat}
                </span>
                <ChevronRight className="w-4 h-4 text-[var(--color-gold)] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          </AnimatedCard>
        ))}
      </div>
    </Section>
  );
}

/* ============================================
   Timeline Preview Section
   ============================================ */

function TimelinePreview() {
  const events = [
    { year: "570 CE", left: "Birth of Prophet Muhammad ﷺ", right: "Aksumite Campaign — Year of the Elephant" },
    { year: "610 CE", left: "First Revelation in Cave of Hira", right: "Byzantine-Sassanid War rages across Middle East" },
    { year: "622 CE", left: "The Hijrah to Madinah", right: "Heraclius launches counteroffensive against Persia" },
    { year: "627 CE", left: "Treaty of Hudaybiyyah", right: "Battle of Nineveh — Heraclius defeats Persia" },
    { year: "630 CE", left: "Conquest of Makkah", right: "True Cross restored to Jerusalem by Heraclius" },
  ];

  return (
    <Section dark className="relative overflow-hidden">
      <div className="absolute inset-0 pattern-star" />
      <div className="relative z-10">
        <SectionHeading
          eyebrow="Parallel Timeline"
          title="Two Worlds, One Story"
          subtitle="While the Prophet ﷺ was building a new civilization, empires were rising and falling across the globe. See how these stories interweave."
          dark
        />

        <div className="mt-16 max-w-4xl mx-auto">
          {events.map((event, i) => (
            <AnimatedCard key={event.year} delay={i * 0.12}>
              <div className="flex items-center gap-4 md:gap-8 mb-8">
                {/* Left — Seerah */}
                <div className="flex-1 text-right">
                  <p className="text-sm md:text-base text-[var(--color-emerald-light)] font-medium">
                    {event.left}
                  </p>
                </div>

                {/* Center — Year */}
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-[var(--color-gold)]/10 border-2 border-[var(--color-gold)]/40 flex items-center justify-center">
                    <span className="text-[10px] md:text-xs font-bold text-[var(--color-gold)]">
                      {event.year.replace(" CE", "")}
                    </span>
                  </div>
                  {i < events.length - 1 && (
                    <div className="w-px h-8 bg-gradient-to-b from-[var(--color-gold)]/40 to-transparent mt-1" />
                  )}
                </div>

                {/* Right — World */}
                <div className="flex-1">
                  <p className="text-sm md:text-base text-[var(--color-cream)]/60">
                    {event.right}
                  </p>
                </div>
              </div>
            </AnimatedCard>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/timeline"
            className="btn-gold inline-flex items-center gap-2"
          >
            Explore Full Timeline
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Section>
  );
}

/* ============================================
   Methodology Section
   ============================================ */

function Methodology() {
  return (
    <Section>
      <div className="max-w-4xl mx-auto">
        <SectionHeading
          eyebrow="Our Approach"
          title="Scholarship, Not Speculation"
          subtitle="Every claim on this platform follows a rigorous verification process rooted in 1,400 years of Islamic scholarly tradition."
        />

        <OrnamentDivider />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              step: "01",
              title: "Isnad Verification",
              description:
                "Every hadith is traced through its chain of narration. Narrator reliability is assessed using classical standards from scholars like Ibn Hajar and Al-Dhahabi.",
            },
            {
              step: "02",
              title: "Cross-Reference",
              description:
                "Claims are checked against the Quran, multiple hadith collections, and scholarly consensus. Contradictions are noted, not hidden.",
            },
            {
              step: "03",
              title: "Source Attribution",
              description:
                "Every piece of content links back to its original source — book, chapter, and verse number. You never have to take our word for it.",
            },
          ].map((item, i) => (
            <AnimatedCard key={item.step} delay={i * 0.15}>
              <div className="text-center">
                <div className="text-5xl font-[var(--font-display)] font-bold text-[var(--color-gold)]/20 mb-3">
                  {item.step}
                </div>
                <h3 className="font-[var(--font-display)] text-xl font-semibold text-[var(--color-midnight)] mb-3">
                  {item.title}
                </h3>
                <p className="text-[var(--color-ink-muted)] text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            </AnimatedCard>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ============================================
   Ask a Scholar CTA
   ============================================ */

function ScholarCTA() {
  return (
    <Section dark className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-navy)] to-[var(--color-midnight)]" />
      <div className="absolute right-0 top-0 w-1/2 h-full bg-[var(--color-gold)]/3 blur-3xl rounded-full -translate-y-1/2" />

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <Sparkles className="w-10 h-10 text-[var(--color-gold)] mx-auto mb-6" />
        <h2 className="font-[var(--font-display)] text-3xl md:text-4xl font-bold text-[var(--color-cream)] mb-4">
          Have a Question About Islam?
        </h2>
        <p className="text-[var(--color-cream)]/60 text-lg mb-8 leading-relaxed">
          Ask a Scholar will answer your questions using only the verified Islamic
          sources in this library, with citations you can check.
          {!SCHOLAR_ENABLED && " Coming soon — until then, the Misconception Clarifier covers the most common questions."}
        </p>
        {SCHOLAR_ENABLED ? (
          <Link to="/scholar" className="btn-gold inline-flex items-center gap-2 text-base px-8 py-3.5">
            <MessageCircle className="w-5 h-5" />
            Ask a Question
          </Link>
        ) : (
          <Link to="/clarifier" className="btn-gold inline-flex items-center gap-2 text-base px-8 py-3.5">
            <MessageCircle className="w-5 h-5" />
            Explore common questions
          </Link>
        )}
      </div>
    </Section>
  );
}

/* ============================================
   Landing Page
   ============================================ */

export default function LandingPage() {
  useSEO();
  usePageView("landing");
  return (
    <main>
      <Hero />
      <Features />
      <TimelinePreview />
      <Section>
        <div className="max-w-2xl mx-auto">
          <DailyVerse />
        </div>
      </Section>
      <Methodology />
      <ScholarCTA />
    </main>
  );
}
